/* eslint-disable max-lines */
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import {
  BooleanFieldMappingEntry,
  ConvertConfig,
  FieldMappingEntry,
  ForeignKeyMapping,
  MaybeNumberFieldMappingEntry,
  NumberFieldMappingEntry,
} from '@adornis/import-export/db/convert-config.js';
import { ImportJob } from '@adornis/import-export/db/import-job.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { convertCSVToEntity } from '@adornis/import-export/server/import-internals.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Language } from '@adornis/translation-core/languages.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { and } from '@adornis/validation/functions/and.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { min } from '@adornis/validation/functions/range.js';
import { readFile, stat, writeFile } from 'fs/promises';
import { DateTime } from 'luxon';
import { join, resolve } from 'path';
import PizZip from 'pizzip';
import { combineLatest, map, merge, switchMap } from 'rxjs';
import { ParticipationState } from '../enums/enums.js';
import { ExportError } from '../errors/ExportError.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { IKHInvoiceReceiverEnum } from '../plugins/stammdaten/ikh-stammdaten-plugin.js';
import { isDefined, lookupEntityById } from '../util.js';
import { Appointment } from './appointment.js';
import { BHFilter } from './bh-filter.js';
import { BHInvoice } from './BHInvoice.js';
import { BHPayment } from './BHPayment.js';
import { Category } from './category.js';
import { Institution } from './institution.js';
import { Participation } from './Participation.js';
import { ParticipationExportCSV } from './ParticipationExportCSV.js';
import { Product } from './products.js';
import { ProductWaitinglistPriorityRule } from './ProductWaitinglistPriorityRule.js';
import { Setting } from './settings.js';
import { UserStammdaten } from './stammdaten.js';
import { Teaching } from './teaching.js';
import { TextMultipleLangItem } from './TextMultipleLangItem.js';
import { User } from './user.js';
import { WorkshopDocument } from './WorkshopDocument.js';
import { WorkshopNote } from './WorkshopNote.js';
import { WorkshopSet } from './WorkshopSet.js';

@Entity()
class AppointmentFieldMapping extends FieldMappingEntry {
  static override _class = 'AppointmentFieldMapping';

  override serialize = appointments => {
    if (!appointments || appointments.length === 0) return '-';
    return appointments
      .map(ap => {
        if (!ap.start || !ap.end) return 'tbd';
        const start = ap.start.toFormat('dd.LL.yyyy HH:mm');
        const end = ap.end.toFormat('dd.LL.yyyy HH:mm');
        return [
          ap._id as string,
          ap.name?.replace(/:/g, '') ?? '',
          ap.location as string,
          `${start as string}-${end as string}`,
        ].join(':');
      })
      .join('//');
  };

  override deserialize = v => {
    if (v === '-') return [];
    return v.split('//').map(str => {
      const l = str.split(':');
      const _id = l.shift();
      const name = l.shift();
      const location = l.shift();
      const m = l.join(':');
      const [_start, _end] = m.split('-');
      const start = _start ? DateTime.fromFormat(_start, 'dd.LL.yyyy HH:mm').toJSDate() : null;
      const end = _end ? DateTime.fromFormat(_end, 'dd.LL.yyyy HH:mm').toJSDate() : null;
      return {
        _class: 'Appointment',
        _id,
        name,
        location,
        start,
        end,
      };
    });
  };
}

@Entity()
export class MaybeStringFieldMappingEntry extends FieldMappingEntry {
  static override _class = 'MaybeStringFieldMappingEntry';

  private defaultValue: string | null = null;

  constructor(doc: PartialEntityData<FieldMappingEntry>, defaultValue?: string) {
    super(doc);
    if (defaultValue) this.defaultValue = defaultValue;
  }

  override deserialize = v => {
    if (v === '') return this.defaultValue;
    return v + '';
  };
}

@Entity()
export class DateFieldMapping extends FieldMappingEntry {
  static override _class = 'DateFieldMapping';

  override deserialize = v => {
    if (!v) return null;
    if (Array.isArray(v)) return v.map(v => DateTime.fromFormat(v, 'dd.LL.yyyy')).join(', ');
    return DateTime.fromFormat(v, 'dd.LL.yyyy');
  };

  override serialize = (v: any) => {
    if (!v) return '';
    return v.toFormat('dd.LL.yyyy');
  };
}

@Entity()
export class AppointmentDateFieldMapping extends FieldMappingEntry {
  static override _class = 'AppointmentDateFieldMapping';

  override deserialize = v => {
    if (!v) return null;
  };

  override serialize = (v: any) => {
    if (!v) return '';
    return v.map(v => v.start.toFormat('dd.LL.yyyy')).join(', ');
  };
}

@Entity()
export class PriceFieldMapping extends FieldMappingEntry {
  static override _class = 'DateFieldMapping';

  override serialize = (v: any) => {
    if (!v) return '';
    return (v / 100).toFixed(2).replace('.', ',') + '€';
  };
}

/**
 * needs special handling if more than one need to be included for one field.
 * Use like this while importing
 * ```
 * new DescriptionFieldMapping({
  input: 'descriptionGermanHTML',
  output: `translatedDescriptionHTML`,
  language: Language.GERMAN,
}),
new DescriptionFieldMapping({
  input: 'descriptionEnglishHTML',
  output: `translatedDescriptionHTML_${Language.ENGLISH}`,
  language: Language.ENGLISH,
}),
// any further langauge need ths _language in output
 * ```
 * 
 * And use like this while exporting
 * ```
 * new DescriptionFieldMapping({
  input: 'descriptionGermanHTML',
  output: 'translatedDescriptionHTML',
  language: Language.GERMAN,
}),
new DescriptionFieldMapping({
  input: 'descriptionEnglishHTML',
  output: 'translatedDescriptionHTML',
  language: Language.ENGLISH,
}),
 * ```
 */
@Entity()
export class DescriptionFieldMapping extends FieldMappingEntry {
  static override _class = 'DescriptionFieldMapping';

  @Field(type => String) language!: Language;

  constructor(doc: PartialEntityData<DescriptionFieldMapping>) {
    super(doc);
  }

  override serialize = (v: TextMultipleLangItem[]) => {
    if (!v || !Array.isArray(v)) return '';
    return v.find(langText => langText.langKey === this.language)?.text ?? '';
  };

  override deserialize = (v: string | undefined, data: Record<string, any>, i: number, entityInitializer: any) => {
    if (!this.output || !v || !this.language) return null;
    const output = this.output.replace(`_${this.language}`, '');
    if (!data[output] || !Array.isArray(data[output])) data[output] = [];
    data[output].push(new TextMultipleLangItem({ text: v, langKey: this.language }).toJSON());
    return data[output];
  };
}

@Entity()
export class ParticipationPaymentDataExportCSV extends AdornisEntity {
  static override _class = 'ParticipationPaymentDataExportCSV';

  @Field(type => String) name!: string;
  @Field(type => String) price!: string;
  @Field(type => Product) product!: Product;
  @Field(type => [Appointment]) appointments!: Appointment[];
  @Field(type => String) invoiceReceiver!: string;
  @Field(type => String) costCenter!: string;
  @Field(type => String) costCenterReceiver!: string;
  @Field(type => String) kostenart!: string;
  @Field(type => DateTime) createdAt!: DateTime;
  @Field(type => String) internalComments!: string;
}

@Entity()
export class WorkshopAffiliation extends AdornisEntity {
  static override _class = 'WorkshopAffiliation';
  @Field(type => String) affiliation!: string; // this is a class name
  @Field(type => String) affiliationID!: string;
}

@Entity()
export class Workshop extends Product {
  static override _class = 'Workshop' as const;

  @Field(type => WorkshopAffiliation)
  affiliation: Maybe<WorkshopAffiliation>;

  @Dependencies({
    classes: () => [WorkshopSet],
    ids: async function (this: PartialEntityData<Workshop>) {
      const affiliationID = this.affiliation?.affiliationID;
      if (affiliationID) return [affiliationID];
      else return [];
    },
  })
  @Field(type => String, {
    resolve: function (this: Workshop) {
      return async () => {
        if (!this.affiliation || this.affiliation.affiliation !== WorkshopSet._class) return null;
        const workshopSet = await getByID(WorkshopSet, this.affiliation.affiliationID)({ name: 1 });
        return workshopSet?.name;
      };
    },
  })
  workshopSetName: Maybe<string>;

  @Field(type => [Appointment], { default: v => v ?? [] })
  appointments!: Appointment[];

  @Dependencies({
    classes: () => [Workshop],
    ids: function (this: PartialEntityData<Workshop>) {
      return [this._id!];
    },
  })
  @Field(type => DateTime, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.startDate !== null && this.startDate !== undefined) return this.startDate;
        const workshop = await getByID(Workshop, this._id)({ appointments: { start: 1 } });
        return workshop?.appointments[0]?.start;
      };
    },
  })
  startDate: Maybe<DateTime>;

  @validate(and(nonOptional(), min(0)))
  @Field(type => Int, { default: v => v ?? 14 })
  capacity!: number;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Workshop>) {
      const participations = await loadMongoData(Participation, { productID: this._id }, { _id: 1 });

      if (!participations) return [];
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        const c = await getRawCollection(Participation._collectionName); // ignore publish filter
        const participationCount = await c.countDocuments({
          productID: this._id,
          state: { $in: [ParticipationState.IN, ParticipationState.PARTICIPATED] },
        });

        return Math.max((this.capacity || 0) - participationCount, 0);
      };
    },
  })
  leftCapacity!: number;

  @Dependencies({
    classes: () => [UserStammdaten],
    ids: async function (this: PartialEntityData<Participation>) {
      const participation = await getByID(Participation, this._id)({ userID: 1, productID: 1 });
      if (!participation) return [];
      const product = await getByID(Workshop, participation.productID)({ _id: 1, institutionID: 1 });
      if (!product) return [];
      const userStammdaten = await loadMongoData(
        UserStammdaten,
        { institutionID: product.institutionID, userID: participation.userID },
        { _id: 1 },
      );

      if (!userStammdaten) return [];
      return userStammdaten.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.highPriorityParticipationCount !== null && this.highPriorityParticipationCount !== undefined)
          return this.highPriorityParticipationCount;

        if (!context?.serverContext) {
          logger.warn(
            'this should never be called from user context. It is to expensive to calculate and does not work for non admin users',
          );
          return null;
        }

        logger.debug('calculate highPriorityParticipationCount');
        const workshop = await loadMongoData(Product, this._id);
        if (!workshop) throw new Error('workshop not found');

        const priorityPlugin = getPluginForScope(
          'registration::product-waitinglist-priority-rules',
          workshop.institutionID,
        );
        if (priorityPlugin.id === 'registration::product-waitinglist-priority-rules') return null;

        const c = await getRawCollection(Participation._collectionName); // ignore publish filter
        const participations = await c
          .find({
            productID: workshop._id,
            state: [ParticipationState.IN, ParticipationState.PARTICIPATED],
          })
          .toArray();

        let highPrioityParticipantsCount = 0;
        for (const participation of participations) {
          if (participation.nachrueckPriority === 'high') highPrioityParticipantsCount++;
        }
        return highPrioityParticipantsCount;
      };
    },
  })
  highPriorityParticipationCount!: number;

  @Dependencies({
    classes: () => [Participation],
    ids: async function (this: PartialEntityData<Workshop>) {
      const participations = await loadMongoData(Participation, { productID: this._id }, { _id: 1 });

      if (!participations) return [];
      return participations.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        const m = await import('../api/participation.js');
        return await m.countWaitingByWorkshop(this._id)();
      };
    },
  })
  waitingParticipantsCount!: number;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  hideCapacity!: boolean;

  @Dependencies({
    classes: () => [ProductWaitinglistPriorityRule],
    ids: async function (this: PartialEntityData<Participation>) {
      const rules = await loadMongoData(ProductWaitinglistPriorityRule, { productID: this._id }, { _id: 1 });
      if (!rules) return [];
      return rules.map(p => p._id);
    },
  })
  @Field(type => Int, {
    resolve: function (this: Workshop) {
      return async () => {
        if (this.nachrueckBlockTimeForLowPriorityInDays) return this.nachrueckBlockTimeForLowPriorityInDays;
        const priorityPlugin = getPluginForScope(
          'registration::product-waitinglist-priority-rules',
          this.institutionID,
        );
        return priorityPlugin.blockTimeForLowPriorityInDays;
      };
    },
  })
  nachrueckBlockTimeForLowPriorityInDays: Maybe<number>;

  @DataLoadedField(type => [WorkshopDocument], ({ _id }) => ({ workshopID: _id }))
  documents?: WorkshopDocument[];

  @DataLoadedField(type => [WorkshopNote], ({ _id }) => ({ workshopID: _id }))
  notes?: WorkshopNote[];

  @Field(type => String)
  detailPage: Maybe<string>;

  get dateIsToBeDetermined() {
    for (const appointment of this.appointments) {
      if (!appointment.start) return true;
    }
    return false;
  }

  get start() {
    if (this.dateIsToBeDetermined) return null;
    const someStartingValue = this.appointments[0]?.start;
    if (!someStartingValue) return null;

    return this.appointments
      .map(ap => ap.start)
      .filter(isDefined)
      .reduce((value, current) => (value > current ? current : value), someStartingValue);
  }

  get end() {
    if (this.dateIsToBeDetermined) return null;
    const someStartingValue = this.appointments[0]?.end;
    if (!someStartingValue) return null;

    return this.appointments
      .map(ap => ap.end)
      .filter(isDefined)
      .reduce((value, current) => (value < current ? current : value), someStartingValue);
  }

  static override async afterUpdate(oldEntity: Workshop, newEntity: Workshop, result: any) {
    if (oldEntity.capacity !== newEntity.capacity) {
      // TODO: move workshop and einzelberatung in buchunsheld entrypoint
      const m = await import('../../../buchungsheld/src/server/waitinglist-monitoring.js');
      await m.nachrueckLogic(newEntity._id);
    }
    return super.afterUpdate(oldEntity, newEntity, result);
  }

  @Subscription(type => String)
  static importWorkshopsForInstitution(
    @Arg('csvString', type => String) csvString: string,
    @Arg('institutionID', type => ID) institutionID: string,
  ) {
    return () => {
      const convertConfig = new ConvertConfig({
        uniqueIdentifierFieldName: '_id',
        className: this._class,
        delimiter: ';',
        fieldMappings: [
          // relations
          { input: 'Kategorie ID', output: 'categoryID' },
          { input: 'ID', output: '_id' },
          new MaybeStringFieldMappingEntry({ input: 'institutionID', output: 'institutionID' }, institutionID),
          { input: 'Semester ID', output: 'timeframeID' },
          // data
          new MaybeStringFieldMappingEntry({ input: 'Name', output: 'name' }),
          new MaybeStringFieldMappingEntry({ input: 'Referent', output: 'speaker' }),
          new NumberFieldMappingEntry({ input: 'Max. Teilnehmer', output: 'capacity' }),
          new MaybeStringFieldMappingEntry({ input: 'sellerID', output: 'sellerID' }),
          new DescriptionFieldMapping({
            input: 'descriptionGermanHTML',
            output: `translatedDescriptionHTML`,
            language: Language.GERMAN,
          }),
          new DescriptionFieldMapping({
            input: 'descriptionEnglishHTML',
            output: `translatedDescriptionHTML_${Language.ENGLISH}`,
            language: Language.ENGLISH,
          }),
          new MaybeNumberFieldMappingEntry({ input: 'sortingWeight', output: 'sortingWeight' }),
          new BooleanFieldMappingEntry({ input: 'hasPrice', output: 'hasPrice' }),
          new MaybeStringFieldMappingEntry({ input: 'certificateID', output: 'certificateID' }),
          new MaybeNumberFieldMappingEntry({ input: 'lastReminderMailSent', output: 'lastReminderMailSent' }),
          new MaybeStringFieldMappingEntry({ input: 'infoLink', output: 'infoLink' }),
          new BooleanFieldMappingEntry({ input: 'isLDAPOnly', output: 'isLDAPOnly' }),
          new BooleanFieldMappingEntry({ input: 'hideCapacity', output: 'hideCapacity' }),
          new MaybeStringFieldMappingEntry({ input: 'reference', output: 'reference' }),
          new MaybeStringFieldMappingEntry({ input: 'terminCopyID', output: 'terminCopyID' }),
          // appointments
          new AppointmentFieldMapping({ input: 'Termine', output: 'appointments' }),
          // price
          { input: 'Preis Class', output: 'prices.0._class' },
          new MaybeStringFieldMappingEntry({ input: 'Preis ID', output: 'prices.0._id' }, A.getGloballyUniqueID()),
          new MaybeStringFieldMappingEntry({ input: 'Stripe ID', output: 'prices.0.stripeID' }),
          { input: 'Währung', output: 'prices.0.currency' },
          new NumberFieldMappingEntry({ input: 'Betrag', output: 'prices.0.unitAmount' }),
        ],
        foreignKeys: [
          new ForeignKeyMapping({
            keyOnHost: 'categoryID',
            uniqueIdentifierFieldName: 'name',
            className: Category._class,
            fieldMappings: [
              new FieldMappingEntry({ input: 'Kategorie', output: 'name' }),
              // new FieldMappingEntry({ input: null, output: null }, (value, data, index, entityInitializer) => {
              //   entityInitializer.institutionID = data.institutionID;
              // }),
            ],
          }),
        ],
      });

      // NOTE: when "ImportJob.convertCSVFromStringToEntity" is called then defaults in "MaybeStringFieldMappingEntry" are not working
      // const workshop = ImportJob.convertCSVFromStringToEntity(csvString, convertConfig)();
      const workshopImportJob = new ImportJob({ convertConfig });
      // workshopImportJob.synchronousHandler( result => {

      // });

      convertCSVToEntity(csvString, convertConfig, workshopImportJob);
      const workshop = workshopImportJob.logger;

      const setting = ImportJob.convertCSVFromStringToEntity(
        csvString,
        new ConvertConfig({
          uniqueIdentifierFieldName: '_id',
          className: Setting._class,
          delimiter: ';',
          fieldMappings: [
            { input: 'Einstellungen ID', output: '_id' },
            { input: 'ID', output: 'productID' },
            { input: 'Kurs Ort', output: 'courseLocation' },
            new MaybeNumberFieldMappingEntry({ input: '1. E-Mail Erinnerung', output: 'firstReminderLeadTime' }),
            new MaybeNumberFieldMappingEntry({ input: '2. E-Mail Erinnerung', output: 'secondReminderLeadTime' }),
            new MaybeNumberFieldMappingEntry({ input: 'Abmeldezeitraum', output: 'blockSignOffDaysInAdvance' }),
            new MaybeNumberFieldMappingEntry({
              input: 'Nicht Aufrücken Zeitraum',
              output: 'blockAutomaticMoveUpDaysInAdvance',
            }),
            new MaybeNumberFieldMappingEntry({
              input: 'QR-Code Gültigkeit Dauer (Minuten)',
              output: 'qrCodeValidityInMinutes',
            }),
          ],
          foreignKeys: [],
        }),
      )();

      const both = merge(workshop, setting);
      return both;
    };
  }

  @Query(type => [Workshop])
  static getAllByTimeframe(@Arg('timeframeID', type => String) timeframeID: string) {
    return async (gqlFields: BaseQLSelectionSet<Workshop>) => {
      const collection = await getCollection<Workshop>(this._class);
      const result = await collection.find<Workshop>({ timeframeID }).toArray();
      return result;
    };
  }

  @Query(type => [Workshop])
  static getAllWorkshopsForTemplate(@Arg('templateID', type => String) templateID: string) {
    return async (gqlFields: string) => {
      const collection = await getCollection<Workshop>(this._class);
      const result = await collection.find<Workshop>({ templateID }).toArray();
      return result;
    };
  }

  @Query(type => [Workshop])
  static getAllWorkshopsForInstitution(@Arg('institutionID', type => String) institutionID: string) {
    return async (gqlFields: BaseQLSelectionSet<Workshop>) => {
      const collection = await getCollection<Workshop>(this._class);
      const result = await collection.find<Workshop>({ institutionID }).toArray();
      return result;
    };
  }

  @Subscription(type => [Workshop])
  static subscribeAllActiveWorkshopsForInstitution(@Arg('institutionID', type => String) institutionID: string) {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      if (!institutionID) return [];

      return combineLatest([
        getCollectionHandle(Workshop._collectionName).watchQuery({ institutionID }),
        getCollectionHandle(Timeframe._collectionName).watchQuery({ institutionID }),
      ]).pipe(switchMap(async () => (await getCollection<Product>(Workshop._class)).find({ institutionID }).toArray()));
    };
  }

  @Query(type => String)
  static exportWorkshopParticipantsCSV(
    @Arg('workshopID', type => ID) workshopID: string,
    @Arg('onlyParticipated', type => Boolean) onlyParticipated: boolean = false,
    @Arg('stammdatenFilter', type => String) stammdatenFilter: Maybe<string> = null,
  ) {
    return async () => {
      const product = await getByID(
        Product,
        workshopID,
      )({ _id: 1, institutionID: 1, appointments: { _id: 1, name: 1 } });
      if (!product) return;

      const participationCollection = await getRawCollection<Participation>(Participation._collectionName);
      let pF = await publishFilter(Participation._class);
      if (context.serverContext) pF = {};
      if (!pF) throw new Error(`Missing publishFilter for ${Participation._class}`);

      const exportData = participationCollection.aggregate<ParticipationExportCSV>([
        {
          $match: {
            $and: [
              { productID: workshopID },
              pF,
              ...(onlyParticipated ? [{ state: ParticipationState.PARTICIPATED }] : []),
            ],
          },
        },
        { $replaceRoot: { newRoot: { participation: '$$ROOT' } } },
        {
          $lookup: {
            from: 'adornisuser',
            localField: 'participation.userID',
            foreignField: '_id',
            as: 'user',
          },
        },
        { $unwind: '$user' },
        {
          $lookup: {
            from: 'user-stammdaten',
            localField: 'user._id',
            foreignField: 'userID',
            as: 'stammdaten',
          },
        },
        { $unwind: '$stammdaten' },
        { $sort: { 'participation.createdAt': -1 } },
        {
          $group: {
            _id: '$user._id',
            participation: { $first: '$participation' },
            user: { $first: '$user' },
            stammdaten: { $first: '$stammdaten' },
          },
        },
        {
          $addFields: {
            _class: ParticipationExportCSV._class,
          },
        },
        {
          $match: stammdatenFilter
            ? Object.entries(JSON.parse(stammdatenFilter)).reduce(
                (acc, [key, value]) => ({ ...acc, ['stammdaten.' + key]: value }),
                {},
              )
            : {},
        },
      ]);

      //get the fields for the export via plugins...
      const plugin = getPluginForScope('registration::stammdaten', product.institutionID);
      const stammdatenFields = plugin.getFields(product.institutionID);
      const lecturersPlugin = getPluginForScope('participation-overview', product.institutionID);
      const fieldsToExport = await lecturersPlugin.getCSVFieldsForExport(stammdatenFields);

      for (const [i, appointment] of Object.entries(product.appointments ?? [])) {
        const input = appointment.name || 'Termin ' + (i + 1);
        const fieldMapping = new FieldMappingEntry(
          { input },
          undefined,
          (v: any, data: ParticipationExportCSV, i: number) =>
            !!data.participation.appointmentParticipations?.find(it => it.appointmentID === appointment._id)
              ? 'Ja'
              : 'Nein',
        );
        fieldsToExport.push(fieldMapping);
      }

      //... then set them here
      const filePath = await exportCSVFromEntities(
        new ConvertConfig<ParticipationExportCSV>({
          className: ParticipationExportCSV._class,
          delimiter: ';',
          fieldMappings: fieldsToExport,
        }),
        exportData,
        'export-teilnehmer',
      );

      try {
        const stats = await stat(filePath);
        if (stats.size === 0) throw new Error('file is empty');
      } catch (e) {
        throw new Error('file is empty');
      }

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  }

  @Query(type => String)
  static ikh_exportWorkshopParticipantsInvoices(@Arg('productID', type => ID) productID: string) {
    return async () => {
      const product = await getByID(Product, productID)({ _id: 1, institutionID: 1 });
      if (!product) throw new Error('product not found ' + productID);

      const invoiceCollection = await getRawCollection(BHInvoice._collectionName);
      const invoices = await invoiceCollection.find<BHInvoice>({ institutionID: product.institutionID }).toArray();

      const zip = new PizZip();
      let fileCount = 0;

      for (const invoice of invoices) {
        const payment = await getByID(BHPayment, invoice.paymentID)(BHPayment.allFields);
        if (!payment) continue;

        const participations = await getByID(
          Participation,
          payment.participationIDs,
        )({
          _id: 1,
          userID: 1,
          user: {
            _id: 1,
            email: 1,
            firstName: 1,
            lastName: 1,
            title: 1,
            username: 1,
            isLdapAccount: 1,
            whatsappNotification: 1,
            whatsappPhone: 1,
            suspentions: { _id: 1, institutionID: 1 },
            receivesNewsletter: 1,
          },
          productID: 1,
          product: {
            _id: 1,
            name: 1,
            institutionID: 1,
            hasPrice: 1,
            prices: { unitAmount: 1, currency: 1 },
          },
          createdAt: 1,
          updatedAt: 1,
          state: 1,
          isNoShow: 1,
          signoffDate: 1,
          registrationDate: 1,
          fulfilledParticipationRequirements: 1,
          lastStateChangeWasInitiatedByAdmin: 1,
          currentPayment: {
            status: 1,
            participationIDs: 1,
            orderNr: { year: 1, nr: 1 },
            amount: 1,
            discount: 1,
          },
          productPriceForUser: { unitAmount: 1, currency: 1 },
          productEntryRequirementLabels: 1,
        });
        const relevantForProduct = participations.filter(p => p.productID === productID).length >= 1;
        if (!relevantForProduct) continue;

        const stammdaten = await UserStammdaten.getExistingStammdaten(
          invoice.userID,
          product.institutionID,
          null,
        )({ _id: 1, invoiceReceiver: 1 } as any);
        if ((stammdaten as any).invoiceReceiver !== IKHInvoiceReceiverEnum.IKHEXTERNAL) continue; // we just want external ones

        const invoiceFile = await getByID(AdornisFile, invoice.pdfID)(AdornisFile.allFields);
        if (!invoiceFile) throw new Error('invoice pdf not found: ' + invoice.pdfID);
        const invoiceFilePath = invoiceFile.getFullPath();
        const dataBuffer = await readFile(invoiceFilePath);
        zip.file(`invoice-${invoice._id}.pdf`, dataBuffer);
        fileCount++;
      }

      if (fileCount === 0) throw new Error('file is empty');

      const data = zip.generate({
        type: 'nodebuffer',
        // compression: DEFLATE adds a compression step.
        // For a 50MB output document, expect 500ms additional CPU time
        compression: 'DEFLATE',
      });

      const importExportConfig = await import('@adornis/import-export/config.js').then(
        module => module.importExportConfig,
      );
      const tmpFilePath = resolve(join(importExportConfig.get('cache'), `invoices-extern-${productID}.zip`));
      await writeFile(tmpFilePath, data);

      const fileName = tmpFilePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  }

  @Query(type => String)
  static exportWorkshopCSV(
    @Arg('institutionID', type => ID) institutionID: string,
    @Arg('timeframeID', type => ID) timeframeID?: string,
  ) {
    return async () => {
      const workshops = (await getRawCollection<Workshop>(this._collectionName)).aggregate<Workshop>([
        ...this.filterWorkshopsByInstitutionAggregationPipeline(institutionID),
        ...(timeframeID ? [{ $match: { timeframeID } }] : []),
        { $lookup: lookupEntityById(Category, 'categoryID', 'category') },
        { $set: { category: { $arrayElemAt: ['$category', 0] } } },
        {
          $lookup: {
            from: Setting._collectionName,
            as: 'settings',
            localField: '_id',
            foreignField: 'productID',
          },
        },
        { $set: { settings: { $arrayElemAt: ['$settings', 0] } } },
      ]);

      const filePath = await exportCSVFromEntities(
        new ConvertConfig<Workshop>({
          className: this._class,
          delimiter: ';',
          fieldMappings: [
            // relations
            { input: 'Kategorie ID', output: 'category._id' },
            { input: 'Kategorie', output: 'category.name' },
            { input: 'ID', output: '_id' },
            { input: 'institutionID', output: 'institutionID' },
            { input: 'Semester ID', output: 'timeframeID' },
            { input: 'Semester', output: 'timeframe.name' },
            // data
            { input: 'Name', output: 'name' },
            { input: 'Referent', output: 'speaker' },
            { input: 'Max. Teilnehmer', output: 'capacity' },
            { input: 'sellerID', output: 'sellerID' },
            new DescriptionFieldMapping({
              input: 'descriptionGermanHTML',
              output: 'translatedDescriptionHTML',
              language: Language.GERMAN,
            }),
            new DescriptionFieldMapping({
              input: 'descriptionEnglishHTML',
              output: 'translatedDescriptionHTML',
              language: Language.ENGLISH,
            }),
            { input: 'sortingWeight', output: 'sortingWeight' },
            { input: 'hasPrice', output: 'hasPrice' },
            { input: 'certificateID', output: 'certificateID' },
            { input: 'lastReminderMailSent', output: 'lastReminderMailSent' },
            { input: 'infoLink', output: 'infoLink' },
            { input: 'isLDAPOnly', output: 'isLDAPOnly' },
            { input: 'hideCapacity', output: 'hideCapacity' },
            { input: 'reference', output: 'reference' },
            { input: 'terminCopyID', output: 'terminCopyID' },
            // appointments
            new AppointmentFieldMapping({ input: 'Termine', output: 'appointments' }),
            // price
            { input: 'Preis Class', output: 'prices.0._class' },
            { input: 'Preis ID', output: 'prices.0._id' },
            new MaybeStringFieldMappingEntry({ input: 'Stripe ID', output: 'prices.0.stripeID' }),
            { input: 'Währung', output: 'prices.0.currency' },
            new NumberFieldMappingEntry({ input: 'Betrag', output: 'prices.0.unitAmount' }),
            /* settings */
            { input: 'Einstellungen ID', output: 'settings._id' },
            { input: 'Kurs Ort', output: 'settings.courseLocation' },
            { input: '1. E-Mail Erinnerung', output: 'settings.firstReminderLeadTime' },
            { input: '2. E-Mail Erinnerung', output: 'settings.secondReminderLeadTime' },
            { input: 'Abmeldezeitraum', output: 'settings.blockSignOffDaysInAdvance' },
            { input: 'Nicht Aufrücken Zeitraum', output: 'settings.blockAutomaticMoveUpDaysInAdvance' },
            { input: 'QR-Code Gültigkeit Dauer (Minuten)', output: 'settings.qrCodeValidityInMinutes' },
          ],
        }),
        workshops,
        'export-workshop',
      );
      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  }

  @Query(type => String)
  static exportWorkshopIkhInternalPaymentDataCSV(
    @Arg('institutionID', type => String) institutionID: string,
    @Arg('start', type => DateTime) start?: DateTime,
    @Arg('end', type => DateTime) end?: DateTime,
  ) {
    return async () => {
      const data = (await getRawCollection<BHPayment>(BHPayment._collectionName)).aggregate<BHPayment>([
        {
          $match: {
            amount: { $ne: [0, null] },
            createdAt: { $gte: start?.toJSDate(), $lt: end?.toJSDate() },
          },
        },
        {
          $unwind: '$products',
        },
        {
          $lookup: {
            from: UserStammdaten._collectionName,
            localField: 'userReference.userID',
            foreignField: 'userID',
            as: 'stammdaten',
          },
        },
        {
          $unwind: '$stammdaten',
        },
        {
          $project: {
            _class: ParticipationPaymentDataExportCSV._class,
            name: '$userReference.userID',
            price: '$amount',
            product: '$products.product',
            invoiceReceiver: '$stammdaten.invoiceReceiver',
            costCenterReceiver: '64050208',
            costCenter: '$stammdaten.costCenter',
            appointments: '$products.product.appointments',
            kostenart: '91100100',
            createdAt: '$createdAt',
            internalComments: '',
          },
        },
        {
          $match: {
            invoiceReceiver: IKHInvoiceReceiverEnum.IKHINTERNAL,
            'product.institutionID': institutionID,
          },
        },
      ]);

      const filePath = await exportCSVFromEntities(
        new ConvertConfig({
          className: ParticipationPaymentDataExportCSV._class,
          delimiter: ';',
          fieldMappings: [
            new MaybeStringFieldMappingEntry({ input: 'Kostenstelle Empfänger', output: 'costCenterReceiver' }),
            new MaybeStringFieldMappingEntry({ input: 'Kostenart', output: 'kostenart' }),
            new MaybeStringFieldMappingEntry({ input: 'Kostenstelle Zahlender', output: 'costCenter' }),
            new FieldMappingEntry(
              { input: 'Buchung' },
              undefined,
              (v: any, data: ParticipationExportCSV, i: number) => 'sofort',
            ),
            new DateFieldMapping({ input: 'Rechnungsversand', output: 'createdAt' }),
            new MaybeStringFieldMappingEntry({ input: 'Name', output: 'product.name' }),
            new AppointmentDateFieldMapping({ input: 'Workshoptag', output: 'appointments' }),
            new PriceFieldMapping({ input: 'Betrag', output: 'price' }),
            new MaybeStringFieldMappingEntry({ input: 'Interne Bemerkungen', output: 'internalComments' }),
          ],
        }),
        data,
        'export-internal-payment-data',
      );

      const fileName = filePath.split('/').slice(-1)[0];
      if (!fileName) throw new ExportError();
      return fileName;
    };
  }

  @Query(type => Int)
  static getAppointmentCount(@Arg('filter', type => BHFilter) filter?: BHFilter) {
    return async () => {
      const c = await getRawCollection(this._collectionName);
      const result = await c
        .aggregate([
          {
            $match: {
              ...(filter?.productID ? { _id: filter?.productID } : {}),
              ...(filter?.institutionID ? { institutionID: filter.institutionID } : {}),
              ...(filter?.timeframeID ? { timeframeID: filter?.timeframeID } : {}),
              ...(filter?.categoryID ? { categoryID: filter?.categoryID } : {}),
            },
          },
          { $unwind: '$appointments' },
          {
            $group: {
              _id: null,
              count: { $sum: 1 },
            },
          },
        ])
        .toArray();

      return result[0]?.count ?? 0;
    };
  }

  private static filterWorkshopsByInstitutionAggregationPipeline(institutionID: string) {
    return [{ $match: { institutionID } }];
  }

  override toString() {
    return this.name;
  }

  static async getAllowedWorkshops(adminAreaOnly: boolean = false) {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const collection = await getRawCollection(Workshop._collectionName);

    if (!adminAreaOnly || user.isSuperAdmin) {
      const result = await collection.find().toArray();
      return result.map(res => res._id);
    }

    const institutionIDs = await Institution.getAllowedInstitutions(true);

    const result = await collection
      .aggregate<{ _id: string }>([
        {
          $match: {
            institutionID: {
              $in: institutionIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    return result.map(res => res._id);
  }
}

export const subscribeWorkshopsByLecturerAndInstitutionIDs = registerSubscription({
  operationName: 'subscribeWorkshopsByLecturerAndInstitutionIDs',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      return watchQuery(Teaching._collectionName, { lecturerID: filter.lecturerID }).pipe(
        switchMap(async teachings => {
          const productIDs = teachings.map(teaching => teaching.productID);
          const workshopCollection = await getRawCollection(Workshop._collectionName);

          return workshopCollection
            .find({
              institutionID: filter.institutionID,
              _id: { $in: productIDs },
            })
            .skip(skip)
            .limit(limit)
            .toArray();
        }),
      );
    };
  },
});

const subscribeLecturerWorkshops = (filter: BHFilter) =>
  watchQuery(Teaching._collectionName, { lecturerID: filter.lecturerID }).pipe(
    switchMap(async teachings => {
      const productIDs = teachings.map(teaching => teaching.productID);
      const workshopCollection = await getRawCollection(Workshop._collectionName);
      const aggregation = [
        { $match: { _id: { $in: productIDs } } }, //
        { $sort: filter.getMongoOptions().sort },
      ];

      return workshopCollection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray();
    }),
  );

const workshopIsFinished = (workshop: Workshop) => {
  if (!workshop.appointments || !workshop.appointments.length || !workshop.appointments.at(-1)?.end) return false; // cant be finish when there are no appointments
  const lastAppointment = workshop.appointments.at(-1)!;
  return lastAppointment.end! < DateTime.now();
};

export const subscribeFinishedWorkshopsForLecturer = registerSubscription({
  operationName: 'subscribeFinishedWorkshopsForLecturer',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      return subscribeLecturerWorkshops(filter).pipe(
        map(workshops => workshops.filter(workshop => workshopIsFinished(workshop as any))),
        map(workshops => workshops.slice(skip, skip + limit)),
      );
    };
  },
});

export const subscribeCurrentWorkshopsForLecturer = registerSubscription({
  operationName: 'subscribeCurrentWorkshopsForLecturer',
  type: () => [Workshop],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number, limit: number, filter: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Workshop>) => {
      return subscribeLecturerWorkshops(filter).pipe(
        map(workshops => workshops.filter(workshop => !workshopIsFinished(workshop as any))),
        map(workshops => workshops.slice(skip, skip + limit)),
      );
    };
  },
});
